import React, { useMemo, useState } from "react";
import {
  DEFAULT_ACCESS_POLICY,
  MODULES,
  getAccessPolicy,
  getUserGroups,
  resetAccessPolicy,
  saveAccessPolicy,
} from "../../auth/permissions";

const ROLES = [
  {
    key: "admin",
    label: "Administrador",
    description: "Acceso completo al sistema. Este rol queda bloqueado por seguridad.",
    locked: true,
  },
  {
    key: "empleados",
    label: "Empleados",
    description: "Usuarios operativos con acceso limitado.",
    locked: false,
  },
  {
    key: "provider",
    label: "Provider",
    description: "Rol reservado para proveedores o usuarios externos.",
    locked: false,
  },
];

export default function AccessControlView() {
  const [policy, setPolicy] = useState(() => getAccessPolicy());
  const [saved, setSaved] = useState(false);

  const currentGroups = useMemo(() => getUserGroups(), []);

  const toggleModule = (role, moduleKey) => {
    if (role === "admin") return;

    setSaved(false);

    setPolicy((prev) => {
      const current = Array.isArray(prev[role]) ? prev[role] : [];
      const exists = current.includes(moduleKey);

      return {
        ...prev,
        [role]: exists
          ? current.filter((m) => m !== moduleKey)
          : [...current, moduleKey],
      };
    });
  };

  const handleSave = async () => {
    await saveAccessPolicy(policy);
    setSaved(true);

    setTimeout(() => {
      window.location.reload();
    }, 600);
  };

  const handleReset = async () => {
    const ok = window.confirm(
      "Esto regresara los permisos a la configuracion default. Deseas continuar?"
    );

    if (!ok) return;

    const restored = await resetAccessPolicy();
    setPolicy(restored);
    setSaved(true);

    setTimeout(() => {
      window.location.reload();
    }, 600);
  };

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <div style={styles.kicker}>Configuracion</div>
          <h1 style={styles.title}>Control de Accesos</h1>
          <p style={styles.subtitle}>
            Administra que modulos puede ver cada rol del sistema. Los grupos vienen de Cognito.
          </p>
        </div>

        <div style={styles.sessionCard}>
          <div style={styles.sessionLabel}>Tus grupos actuales</div>
          <div style={styles.sessionGroups}>
            {currentGroups.length ? currentGroups.join(", ") : "sin grupo detectado"}
          </div>
        </div>
      </div>

      <div style={styles.notice}>
        <strong>Regla de seguridad:</strong> el rol admin siempre conserva acceso completo.
        Esta pantalla controla visibilidad y rutas del frontend. Para seguridad completa de APIs,
        despues se debe respaldar esta politica en backend.
      </div>

      <div style={styles.grid}>
        {ROLES.map((role) => {
          const allowed = Array.isArray(policy[role.key]) ? policy[role.key] : [];

          return (
            <section key={role.key} style={styles.card}>
              <div style={styles.cardHead}>
                <div>
                  <h2 style={styles.roleTitle}>{role.label}</h2>
                  <p style={styles.roleDesc}>{role.description}</p>
                </div>

                {role.locked && <span style={styles.lockedPill}>Bloqueado</span>}
              </div>

              <div style={styles.moduleList}>
                {MODULES.map((module) => {
                  const checked =
                    role.key === "admin" || allowed.includes(module.key);

                  return (
                    <label
                      key={`${role.key}-${module.key}`}
                      style={{
                        ...styles.moduleRow,
                        opacity: role.locked ? 0.72 : 1,
                        cursor: role.locked ? "not-allowed" : "pointer",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={role.locked}
                        onChange={() => toggleModule(role.key, module.key)}
                      />
                      <span>{module.label}</span>
                    </label>
                  );
                })}
              </div>
            </section>
          );
        })}
      </div>

      <div style={styles.actions}>
        <button type="button" onClick={handleReset} style={styles.secondaryBtn}>
          Restaurar default
        </button>

        <button type="button" onClick={handleSave} style={styles.primaryBtn}>
          Guardar permisos
        </button>
      </div>

      {saved && (
        <div style={styles.saved}>
          Permisos guardados. Recargando interfaz...
        </div>
      )}
    </div>
  );
}

const styles = {
  page: {
    maxWidth: 1180,
    margin: "0 auto",
  },
  header: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 20,
    marginBottom: 18,
  },
  kicker: {
    fontSize: 12,
    fontWeight: 900,
    letterSpacing: ".12em",
    textTransform: "uppercase",
    color: "#64748b",
  },
  title: {
    margin: "6px 0 0",
    fontSize: 34,
    letterSpacing: "-.04em",
    color: "#0f172a",
  },
  subtitle: {
    marginTop: 8,
    color: "#64748b",
    fontWeight: 650,
  },
  sessionCard: {
    minWidth: 260,
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 18,
    padding: 16,
    boxShadow: "0 12px 28px rgba(15,23,42,.06)",
  },
  sessionLabel: {
    fontSize: 12,
    fontWeight: 900,
    color: "#64748b",
    textTransform: "uppercase",
    letterSpacing: ".10em",
  },
  sessionGroups: {
    marginTop: 8,
    fontWeight: 900,
    color: "#0f172a",
  },
  notice: {
    background: "#eff6ff",
    border: "1px solid #bfdbfe",
    color: "#1e3a8a",
    borderRadius: 16,
    padding: 16,
    marginBottom: 18,
    fontWeight: 650,
    lineHeight: 1.5,
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: 18,
  },
  card: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 20,
    padding: 18,
    boxShadow: "0 12px 28px rgba(15,23,42,.06)",
  },
  cardHead: {
    minHeight: 96,
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
    borderBottom: "1px solid #e5e7eb",
    paddingBottom: 14,
    marginBottom: 14,
  },
  roleTitle: {
    margin: 0,
    fontSize: 20,
    color: "#111827",
  },
  roleDesc: {
    margin: "6px 0 0",
    color: "#64748b",
    fontSize: 13,
    lineHeight: 1.45,
    fontWeight: 650,
  },
  lockedPill: {
    fontSize: 11,
    fontWeight: 900,
    color: "#92400e",
    background: "#fef3c7",
    border: "1px solid #fde68a",
    borderRadius: 999,
    padding: "4px 9px",
  },
  moduleList: {
    display: "grid",
    gap: 10,
  },
  moduleRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    background: "#f8fafc",
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    padding: "10px 12px",
    fontWeight: 800,
    color: "#334155",
  },
  actions: {
    marginTop: 20,
    display: "flex",
    justifyContent: "flex-end",
    gap: 12,
  },
  secondaryBtn: {
    border: "1px solid #cbd5e1",
    background: "#fff",
    color: "#334155",
    borderRadius: 14,
    padding: "12px 16px",
    fontWeight: 900,
    cursor: "pointer",
  },
  primaryBtn: {
    border: "1px solid #1d4ed8",
    background: "#1d4ed8",
    color: "#fff",
    borderRadius: 14,
    padding: "12px 18px",
    fontWeight: 900,
    cursor: "pointer",
  },
  saved: {
    marginTop: 14,
    background: "#ecfdf5",
    border: "1px solid #bbf7d0",
    color: "#166534",
    borderRadius: 14,
    padding: 14,
    fontWeight: 900,
  },
};
